import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** In-store TV board. Opens on any HDMI stick browser, refreshes itself every
 *  minute, so the owner only ever updates Today's Gold Rate on his phone. */
export default function RateBoard() {
  const { branch } = useParams();
  const [data, setData] = useState(null);
  const [clock, setClock] = useState("");

  const load = useCallback(() => {
    axios
      .get(`${API}/public/display/${branch}`)
      .then(({ data: d }) => setData(d))
      .catch(() => {});
  }, [branch]);

  useEffect(() => {
    load();
    const rates = setInterval(load, 60000);
    const tick = setInterval(
      () =>
        setClock(
          new Date().toLocaleTimeString("en-AU", {
            timeZone: "Australia/Melbourne",
            hour: "numeric",
            minute: "2-digit",
          })
        ),
      1000
    );
    return () => {
      clearInterval(rates);
      clearInterval(tick);
    };
  }, [load]);

  if (!data) return <div className="h-screen bg-[#FBF7EF]" />;

  const rows = Math.max(data.rates.length, 1);
  // Rows must always fit the screen height, however many purities are listed.
  const rowFont = `min(5vw, ${(48 / rows).toFixed(1)}vh)`;
  const unitFont = `min(1.5vw, ${(14 / rows).toFixed(1)}vh)`;

  return (
    <div
      className="relative flex h-screen flex-col overflow-hidden bg-[#FBF7EF] text-[#2A2318]"
      data-testid="rate-board"
    >
      {/* warm champagne wash + soft gold corner glows */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(120vh 90vh at 12% -10%, rgba(197,160,89,0.22), transparent 60%)," +
            "radial-gradient(110vh 80vh at 100% 110%, rgba(197,160,89,0.16), transparent 62%)",
        }}
      />

      <div className="relative flex h-full flex-col px-[3.5vw] py-[2.8vh]">
        <header className="flex items-center justify-between gap-[2vw]">
          <div className="flex items-center gap-[1.4vw]">
            <img
              src="/brand/logo.png"
              alt="Dhunna Jewellers"
              className="w-auto shrink-0 object-contain"
              style={{ height: "min(6.5vw, 12vh)", mixBlendMode: "multiply" }}
              data-testid="rate-board-logo"
            />
            <div>
              <p
                className="font-invoice uppercase leading-none tracking-[0.2em] text-[#2A2318]"
                style={{ fontSize: "2.1vw" }}
              >
                Dhunna Jewellers
              </p>
              <p
                className="mt-[0.8vh] uppercase tracking-[0.34em] text-[#8A7440]"
                style={{ fontSize: "1vw" }}
              >
                {data.branch}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="tabular font-semibold leading-none text-[#2A2318]" style={{ fontSize: "2vw" }}>
              {clock}
            </p>
            <p
              className="mt-[0.8vh] uppercase tracking-[0.22em] text-[#8A7440]/80"
              style={{ fontSize: "0.95vw" }}
              data-testid="rate-board-updated"
            >
              {data.as_at
                ? `Updated ${data.as_at}${data.as_at_time ? ` · ${data.as_at_time}` : ""}`
                : data.today}
            </p>
          </div>
        </header>

        <div className="mt-[2.4vh] flex items-center gap-[1.2vw]">
          <span
            className="font-invoice uppercase tracking-[0.16em] text-[#B08D45]"
            style={{ fontSize: "1.5vw" }}
          >
            {!data.is_today && data.as_at
              ? `Gold Rates — as at ${data.as_at}`
              : "Today's Gold Rates"}
          </span>
          <span className="h-[1px] flex-1 bg-gradient-to-r from-[#C5A059] to-transparent" />
          {!data.is_today && data.as_at && (
            <span
              className="uppercase tracking-[0.2em] text-[#B24A3A]"
              style={{ fontSize: "0.95vw" }}
              data-testid="rate-board-stale"
            >
              Not today&apos;s rates — please ask our staff
            </span>
          )}
        </div>

        <main className="mt-[2vh] min-h-0 flex-1">
          {data.rates.length === 0 ? (
            <p
              className="flex h-full items-center justify-center text-[#8A7440]/70"
              style={{ fontSize: "2vw" }}
            >
              Rates will appear here once set.
            </p>
          ) : (
            <div className="flex h-full flex-col justify-center gap-[1.2vh]">
              {data.rates.map((r) => (
                <div
                  key={r.purity}
                  className="flex items-center justify-between rounded-[0.5vw] border border-[#E4D9BF] bg-white/75 px-[2vw] py-[0.9vh] shadow-[0_1px_0_rgba(197,160,89,0.25)]"
                  data-testid={`rate-row-${r.purity}`}
                >
                  <span className="flex items-center gap-[1.6vw]">
                    <span
                      className="block w-[0.4vw] shrink-0 rounded-full bg-[#C5A059]"
                      style={{ height: `calc(${rowFont} * 0.8)` }}
                    />
                    <span
                      className="font-invoice tracking-[0.04em] text-[#2A2318]"
                      style={{ fontSize: rowFont, lineHeight: 1.1 }}
                    >
                      {r.purity}
                    </span>
                  </span>
                  <span
                    className="tabular font-semibold text-[#9A7822]"
                    style={{ fontSize: rowFont, lineHeight: 1.1 }}
                  >
                    $
                    {r.rate.toLocaleString("en-AU", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    <span
                      className="ml-[0.8vw] font-normal uppercase tracking-[0.18em] text-[#8A7440]/70"
                      style={{ fontSize: unitFont }}
                    >
                      / gram
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </main>

        <footer className="mt-[2vh] border-t border-[#E4D9BF] pt-[1.4vh] text-center">
          <p className="text-[#6F6349]" style={{ fontSize: "1vw" }}>
            {data.disclaimer}
          </p>
          {data.phone && (
            <p
              className="mt-[0.5vh] tracking-[0.25em] text-[#B08D45]"
              style={{ fontSize: "1vw" }}
            >
              {data.phone}
            </p>
          )}
          <p
            className="mt-[0.6vh] tracking-[0.2em] text-[#6F6349]/50"
            style={{ fontSize: "0.8vw" }}
            data-testid="rate-board-credit"
          >
            Designed by Everindigital.com.au
          </p>
        </footer>
      </div>
    </div>
  );
}
